<script setup lang="ts">
import api from '@/api';
import dayjs from '@/helpers/dayjs';
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import { useAsyncState, useMediaQuery, watchDebounced } from '@vueuse/core';
import { omit } from 'lodash-es';
import { useRouter } from 'vue-router';
import { PencilSquareIcon } from '@heroicons/vue/24/outline';
import {
  CustomerErhlLoanPurposeOptions,
  ERHL_MIN_LOAN_AMOUNT,
  ErhlEquityAccessMethod,
  ErhlLoanPurpose,
} from '@/helpers/const';
import { defaultAdditionalFundRequired } from '@/helpers/builder';
import { ADDITIONAL_PURPOSES_NO, resolveErhlLoanPurposes } from '@/helpers/erhlLoanPurposes';
import { formatCurrency, snakeCase } from '@/helpers/format';
import { INVALID_JWT_ERROR, useSessionExpiry } from '@/helpers/useSessionExpiry';
import { useCalcApi } from '@/helpers/useCalcApi';
import { useErhlCustomerLoanAppProcess } from '@/helpers/useErhlCustomerLoanAppProcess';
import { erhlLoanDetailsSchema } from '@/validation';
import { getMinDateAfterBusinessDays, getUpcomingBusinessDays } from '@/helpers/businessDays';
import { TippyDirective } from '@/helpers/tippy';
import { shouldShowEquitySnapshot } from '@/helpers/erhlEquitySnapshot';
import EquitySnapshot from './modules/LoanDetails/EquitySnapshot.vue';
import { getFundsRequiredEditRoute } from '@/helpers/erhlCustomerPropertyDetails';

import type { ErhlLoanPurposeValue } from '@/helpers/const';
import type { AdditionalFundsRequired, ErhlDrawdown } from '@/types';

const { state, loanApp, goBack, saveAndGoNext } = useErhlCustomerLoanAppProcess();
const { flagSessionExpired } = useSessionExpiry();
const isLoading = ref(false);
const isDesktop = useMediaQuery('(min-width: 768px)');
const calc = useCalcApi();
const vTippy = TippyDirective;
const releaseAmountTooltip = 'How much would you like to receive via scheduled equity release each month?';
const fundsRequiredByTooltip =
  'Please allow at least 5 business days for us to process the application before settlement.';
const releaseTermTooltip = 'How long would you like to receive scheduled equity release for?';
const scheduledEquityReleaseTooltip =
  'Do you want to access more of your available equity via an ongoing scheduled payment?';
const initialLumpSumTooltip = 'How much would you like to release as a lump sum? Minimum loan amount is $300,000.';
const fundsRequiredAmountTooltip = 'Calculated from the property purchase details provided earlier.';
const additionalBorrowingPurposesTooltip = 'Are there any additional funds required for other borrowing purposes?';
const existing = loanApp.value.erhlDrawdowns ?? [];
const existingLumpSum = existing.find((d) => d.drawdownType === 'lump_sum');
const existingScheduledEquityRelease = existing.find((d) => d.drawdownType === 'scheduled_equity_release');

const { state: listAdditionalFundsPurposes } = useAsyncState(
  () => api.backend.public.listAdditionalFundsPurposes(),
  [],
);

/**
 * Resolves the real backend purpose GUID for the selected additional borrowing purpose,
 * mirroring the same lookup the standard (non-ERHL) loan details flow uses. Falls back to
 * an empty string (persisted as null) when no matching reference-data purpose is found,
 * rather than a fabricated id.
 */
const resolveAdditionalFundsPurposeId = (purpose?: ErhlLoanPurposeValue): string =>
  listAdditionalFundsPurposes.value?.find((item) => snakeCase(item.title) === purpose)?.id ?? '';

/**
 * Additional purpose options: all ERHL purposes except Purchase New Property,
 * with a leading "No" sentinel option.
 */
const additionalPurposeOptions = computed(() => {
  return {
    [ADDITIONAL_PURPOSES_NO]: 'No',
    ...omit(CustomerErhlLoanPurposeOptions, ErhlLoanPurpose.PURCHASE_NEW_PROPERTY),
  };
});

const existingAdditionalPurposes = computed<string[]>(() => {
  if (!isPurchaseApplication.value) return [ADDITIONAL_PURPOSES_NO];

  const purposes = loanApp.value.erhlLoanPurposes ?? [];
  const additional = purposes.filter((p) => p !== ErhlLoanPurpose.PURCHASE_NEW_PROPERTY);
  if (additional.length > 0) return additional;

  const hasAdditionalBorrowing = (existingLumpSum?.amount ?? 0) > 0 || !!loanApp.value.erhlLoanPurposeOtherDescription;
  return hasAdditionalBorrowing ? [ErhlLoanPurpose.OTHER] : [ADDITIONAL_PURPOSES_NO];
});

/** Whether "Purchase new property" is among the selected loan purposes */
const isPurchaseApplication = computed(() =>
  (loanApp.value.erhlLoanPurposes ?? []).includes(ErhlLoanPurpose.PURCHASE_NEW_PROPERTY),
);

const router = useRouter();

const editFundsRequired = () => router.push(getFundsRequiredEditRoute(loanApp.value));

/**
 * Initial "Scheduled equity release?" answer, seeded from the loan purpose step's equity
 * access method. The live answer lives on `values.hasScheduledEquityRelease` from here on —
 * this is only consulted once, to seed the form's initial value.
 */
const initialHasScheduledEquityRelease = computed(
  () => loanApp.value.erhlEquityAccessMethod === ErhlEquityAccessMethod.LUMP_SUM_AND_MONTHLY_DRAWDOWN,
);

/** Whether the applicant entered a non-zero "additional contributions amount" on the property details page */
const hasAdditionalContributions = computed(() => (loanApp.value.incomingProperties?.[0]?.erhlSavingsAmount ?? 0) > 0);

/** Additional borrowing purposes/amount are suppressed entirely once contributions already cover the gap */
const showAdditionalBorrowingPurposesQuestion = computed(
  () => isPurchaseApplication.value && !hasAdditionalContributions.value,
);

/** Minimum selectable date for funds required by: 5 business days from today */
const minFundsRequiredByDate = computed(() => getMinDateAfterBusinessDays(5));

/** The 5 business days (inclusive of today) that are unavailable for settlement */
const unavailableFundsRequiredByDates = computed(() => getUpcomingBusinessDays(5));

/** Age in whole years of the oldest applicant, based on their birthdate */
const oldestApplicantAge = computed(() => {
  const ages = loanApp.value.applicants
    .map((a) => (a.birthdate ? dayjs().diff(dayjs(a.birthdate), 'year') : 0))
    .filter((age) => age > 0);
  return ages.length > 0 ? Math.max(...ages) : 0;
});

/**
 * Dynamic maximum scheduled equity release duration (years) based on oldest borrower age.
 * - age <= 60: max = 30 years
 * - age 61–80: max = 30 - (age - 60)
 * - age > 80:  max = 10 years
 */
const scheduledEquityReleaseMaxDuration = computed(() => {
  const age = oldestApplicantAge.value;
  if (age > 80) return 10;
  if (age > 60) return 30 - (age - 60);
  return 30;
});

/** Schema rebuilt reactively so the duration max reflects the oldest applicant's age */
const validationSchema = computed(() =>
  erhlLoanDetailsSchema(scheduledEquityReleaseMaxDuration.value, hasAdditionalContributions.value),
);

const {
  handleSubmit: validateSubmit,
  values,
  meta,
  submitCount,
  setFieldValue,
} = useForm({
  validationSchema,
  initialValues: {
    erhlAdditionalBorrowingPurposes: existingAdditionalPurposes.value,
    erhlLoanPurposeOtherDescription: loanApp.value.erhlLoanPurposeOtherDescription ?? '',
    // Prefer additionalFundsRequired (current storage); fall back to the legacy lump_sum
    // drawdown for drafts saved before this field stopped being conflated with a drawdown.
    erhlAdditionalAmountToBorrow:
      (isPurchaseApplication.value ? loanApp.value.additionalFundsRequired?.fundsRequiredAmount : undefined) ??
      existingLumpSum?.amount ??
      undefined,
    hasScheduledEquityRelease: initialHasScheduledEquityRelease.value,
    scheduledEquityReleaseAmount: existingScheduledEquityRelease?.amount ?? 1_000,
    // The wire field `durationTotalMonths` genuinely holds months; this form's slider UX is in
    // years, so convert months -> years here for the initial value, and years -> months again
    // when building the payload on submit / calc-preview below.
    durationTotalYears: existingScheduledEquityRelease?.durationTotalMonths
      ? Math.round(existingScheduledEquityRelease.durationTotalMonths / 12)
      : 10,
    lumpSumFundsRequiredBy: existingLumpSum?.fundsRequiredBy ?? undefined,
    lumpSumAmount: existingLumpSum?.amount ?? ERHL_MIN_LOAN_AMOUNT,
    isPurchaseApplication: isPurchaseApplication.value,
  },
  validateOnMount: false,
});

const hasAdditionalPurposes = computed(
  () =>
    Array.isArray(values.erhlAdditionalBorrowingPurposes) &&
    values.erhlAdditionalBorrowingPurposes.length > 0 &&
    !values.erhlAdditionalBorrowingPurposes.includes(ADDITIONAL_PURPOSES_NO),
);

/** Additional amount to borrow that actually counts toward the loan — zero when the question is suppressed or unanswered */
const effectiveAdditionalAmountToBorrow = computed(() =>
  showAdditionalBorrowingPurposesQuestion.value && hasAdditionalPurposes.value
    ? values.erhlAdditionalAmountToBorrow ?? 0
    : 0,
);

/** Whether 'Other' is among the selected additional borrowing purposes */
const hasOtherPurpose = computed(() => (values.erhlAdditionalBorrowingPurposes ?? []).includes(ErhlLoanPurpose.OTHER));

/**
 * Keeps "No" and the real purposes mutually exclusive.
 * - If "No" was just added alongside real purposes, clear down to just "No".
 * - If a real purpose was added while "No" was already selected, remove "No".
 */
watch(
  () => values.erhlAdditionalBorrowingPurposes,
  (newVal, oldVal) => {
    if (!Array.isArray(newVal) || newVal.length <= 1) return;
    if (!newVal.includes(ADDITIONAL_PURPOSES_NO)) return;

    const noWasPreviouslySelected = Array.isArray(oldVal) && oldVal.includes(ADDITIONAL_PURPOSES_NO);

    if (!noWasPreviouslySelected) {
      setFieldValue('erhlAdditionalBorrowingPurposes', [ADDITIONAL_PURPOSES_NO]);
    } else {
      setFieldValue(
        'erhlAdditionalBorrowingPurposes',
        newVal.filter((p) => p !== ADDITIONAL_PURPOSES_NO),
      );
    }
  },
);

const { state: baseLoanAmount, isReady } = useAsyncState(calc.getLoanAmount({ ...loanApp.value }), 0);

const { state: estimatedEquityAvailable, isReady: isEquityReady } = useAsyncState(
  calc.getMaxLoanAmount({ ...loanApp.value }),
  0,
);

const showEquitySnapshot = computed(() =>
  shouldShowEquitySnapshot(estimatedEquityAvailable.value, isEquityReady.value),
);

/**
 * Initial lump sum slider max: higher of $500,000 or 125% of estimated equity available, rounded to
 * the nearest $10,000. The floor sits above the $300,000 minimum so the slider keeps a usable range
 * for low-equity applicants instead of collapsing to min === max.
 */
const initialLumpSumSliderMax = computed(() =>
  Math.max(ERHL_MIN_LOAN_AMOUNT + 200_000, Math.round((estimatedEquityAvailable.value * 1.25) / 10_000) * 10_000),
);

/** Settlement fees and charges, and set-up fee % — sourced from earlier steps, not this page's sliders */
const { state: settlementFeeAmount, isReady: isSettlementFeeReady } = useAsyncState(
  calc.getSettlementFee({ ...loanApp.value }),
  0,
);
const { state: setupFeeRate, isReady: isSetupFeeRateReady } = useAsyncState(
  calc.getSetupFeeRate({ ...loanApp.value }),
  0,
);

/**
 * Total scheduled equity release — unlike the calls above, this depends on the release
 * amount/term entered live on *this* page, which aren't written back to `loanApp` until submit.
 * Re-fetches from the same calc service that populates the CRM's total scheduled equity release
 * whenever those inputs change.
 */
const {
  state: totalScheduledEquityRelease,
  isReady: isTotalScheduledEquityReleaseReady,
  execute: fetchTotalScheduledEquityRelease,
} = useAsyncState(
  () =>
    values.hasScheduledEquityRelease
      ? calc.getTotalScheduledEquityRelease(loanApp.value, {
          amount: values.scheduledEquityReleaseAmount ?? 0,
          durationTotalMonths: (values.durationTotalYears ?? 0) * 12,
        })
      : Promise.resolve(0),
  0,
  { immediate: true, resetOnExecute: false },
);
watchDebounced(
  () => [values.hasScheduledEquityRelease, values.scheduledEquityReleaseAmount, values.durationTotalYears],
  () => fetchTotalScheduledEquityRelease(),
  { debounce: 300 },
);

/**
 * Sticky "has loaded at least once" flag: `isTotalScheduledEquityReleaseReady` itself flips
 * false/true on every debounced re-fetch triggered by slider input, which would otherwise flash
 * the equity snapshot bar and summary rows back to a spinner on every adjustment. This only ever
 * flips true, keeping the UI in its "ready" state through subsequent background refetches.
 */
const hasLoadedTotalScheduledEquityRelease = ref(false);
watch(
  isTotalScheduledEquityReleaseReady,
  (ready) => {
    if (ready) hasLoadedTotalScheduledEquityRelease.value = true;
  },
  { immediate: true },
);

/** Number of monthly scheduled equity release payments: matches the calcs formula's `years*12 - 1` */
const numberOfScheduledPayments = computed(() => (values.durationTotalYears ?? 0) * 12 - 1);

/**
 * Estimated amount to clear mortgages on supporting properties. For ERHL applications,
 * `MortgagePayoutEstimateFormulaService` operates on `supportingProperties` (not a raw
 * `outstandingLoanAmount` sum) — same underlying formula the purchase branch already relies on
 * indirectly via `baseLoanAmount` → loan-amount → funds-to-complete-purchase.
 */
const { state: mortgagePayoutEstimate, isReady: isMortgagePayoutEstimateReady } = useAsyncState(
  calc.getMortgagePayoutEstimate({ ...loanApp.value }),
  0,
);

const isEstimatedTotalAmountOwedReady = computed(
  () =>
    isReady.value &&
    hasLoadedTotalScheduledEquityRelease.value &&
    isSettlementFeeReady.value &&
    isSetupFeeRateReady.value &&
    isMortgagePayoutEstimateReady.value,
);

/**
 * Estimated principal balance at end of loan term.
 * - Non-purchase: grosses up lump sum + scheduled release + mortgage payout estimate + settlement
 *   fees by the set-up fee rate, then rounds up to the nearest thousand.
 * - Purchase: the funds required amount already bakes in the set-up fee (see loan-amount
 *   formula), so this is a plain sum of the three summary rows — no further grossing/rounding.
 */
const estimatedTotalAmountOwed = computed(() => {
  if (!isPurchaseApplication.value) {
    const lumpSum = values.lumpSumAmount ?? 0;
    const base = lumpSum + totalScheduledEquityRelease.value + mortgagePayoutEstimate.value + settlementFeeAmount.value;
    const rate = setupFeeRate.value ?? 0;
    const total = base + base * (rate / (1 - rate));
    return Math.ceil(total / 1000) * 1000;
  }

  return baseLoanAmount.value + effectiveAdditionalAmountToBorrow.value + totalScheduledEquityRelease.value;
});

/** How far the estimated total owed exceeds estimated equity available (may be negative) */
const equityOverAmount = computed(() => estimatedTotalAmountOwed.value - estimatedEquityAvailable.value);

/** Non-blocking warning: true when the estimated total owed exceeds estimated equity available */
const showEquityWarning = computed(() => {
  if (!isEstimatedTotalAmountOwedReady.value || !isEquityReady.value || !showEquitySnapshot.value) return false;
  return equityOverAmount.value > 0;
});

/** Non-blocking advisory: amount above the $12,500 monthly policy maximum */
const showReleaseAmountPolicyNotice = computed(() => (values.scheduledEquityReleaseAmount ?? 0) > 12_500);

/** Non-blocking advisory: duration below the 10-year policy minimum */
const showReleaseTermPolicyNotice = computed(
  () => (values.durationTotalYears ?? 0) > 0 && (values.durationTotalYears ?? 0) < 10,
);

const handleSubmit = validateSubmit(async (formValues) => {
  isLoading.value = true;

  const drawdowns: ErhlDrawdown[] = [];

  // Purchase applications capture "additional funds for other borrowing purposes" via
  // additionalFundsRequired below, not as a drawdown — erhlDrawdowns represents how the
  // ERHL principal is disbursed (lump sum vs scheduled), not why funds are needed.
  if (!isPurchaseApplication.value) {
    const lumpSumAmount = formValues.lumpSumAmount ?? 0;
    if (lumpSumAmount > 0) {
      drawdowns.push({
        drawdownType: 'lump_sum',
        effectiveFrom: existingLumpSum?.effectiveFrom ?? undefined,
        amount: lumpSumAmount,
        fundsRequiredBy: formValues.lumpSumFundsRequiredBy ?? undefined,
      });
    }
  }

  if (formValues.hasScheduledEquityRelease) {
    drawdowns.push({
      drawdownType: 'scheduled_equity_release',
      effectiveFrom: existingScheduledEquityRelease?.effectiveFrom ?? undefined,
      amount: formValues.scheduledEquityReleaseAmount ?? 0,
      // The wire field `durationTotalMonths` genuinely holds months; convert the form's
      // years-based slider value here before it leaves this component.
      durationTotalMonths: (formValues.durationTotalYears ?? 0) * 12,
      paymentFrequency: 'monthly',
    });
  }

  // The "Scheduled equity release?" answer here is the source of truth for equity access
  // method going forward — changing it updates the loan purpose step too, since both read/write
  // the same shared `erhlEquityAccessMethod` field on loanApp.
  const erhlEquityAccessMethod = formValues.hasScheduledEquityRelease
    ? ErhlEquityAccessMethod.LUMP_SUM_AND_MONTHLY_DRAWDOWN
    : ErhlEquityAccessMethod.LUMP_SUM_ONLY;

  const selectedAdditional = showAdditionalBorrowingPurposesQuestion.value
    ? ((formValues.erhlAdditionalBorrowingPurposes ?? []).filter(
        (p) => p !== ADDITIONAL_PURPOSES_NO,
      ) as ErhlLoanPurposeValue[])
    : [];

  const erhlLoanPurposes = resolveErhlLoanPurposes({
    isPurchaseApplication: isPurchaseApplication.value,
    showAdditionalBorrowingPurposesQuestion: showAdditionalBorrowingPurposesQuestion.value,
    selectedAdditional,
    existingErhlLoanPurposes: loanApp.value.erhlLoanPurposes ?? [],
  });

  // Only keep the description while "Other" is still selected, so deselecting it doesn't leave
  // stale text persisted on the draft.
  const purposeDescription = isPurchaseApplication.value
    ? selectedAdditional.includes(ErhlLoanPurpose.OTHER)
      ? formValues.erhlLoanPurposeOtherDescription
      : ''
    : loanApp.value.erhlLoanPurposeOtherDescription;

  // "Additional amount" answers the "other borrowing purposes" question directly via the
  // shared additionalFundsRequired entity (same as non-ERHL loans) — only the multi-select's
  // first real purpose resolves a purpose id, since additionalFundsPurposeId is a single FK.
  const additionalAmount =
    showAdditionalBorrowingPurposesQuestion.value && hasAdditionalPurposes.value
      ? formValues.erhlAdditionalAmountToBorrow ?? 0
      : 0;
  const additionalFundsRequired: AdditionalFundsRequired | undefined =
    additionalAmount > 0
      ? Object.assign(defaultAdditionalFundRequired(), {
          isAdditionalFundsRequired: true,
          fundsRequiredAmount: additionalAmount,
          description: purposeDescription ?? '',
          additionalFundsPurposeId: resolveAdditionalFundsPurposeId(selectedAdditional[0]),
        })
      : undefined;

  const applicationDraftId = loanApp.value.applicationDraftId;
  try {
    saveAndGoNext({
      erhlLoanPurposes,
      erhlLoanPurposeOtherDescription: purposeDescription,
      erhlEquityAccessMethod,
      erhlDrawdowns: drawdowns,
      erhlHasScheduledEquityRelease: formValues.hasScheduledEquityRelease,
      additionalFundsRequired,
      applicationDraftId,
    });
  } catch (error) {
    if (api.getErrorMsg(error) === INVALID_JWT_ERROR) {
      flagSessionExpired();
      return;
    }
    console.error('Failed to save draft:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div>
    <div class="mb-8 pb-2">
      <h2 class="text-2xl font-medium">Your loan <BxHighlight>details</BxHighlight></h2>
    </div>
    <div class="section-panel md:max-w-[50%] md:p-6">
      <div
        v-if="showEquitySnapshot"
        data-test-id="equity-snapshot-desktop"
        class="sticky top-[68px] z-10 mb-6 hidden rounded-t-lg bg-[#F7FEF9] md:-mx-6 md:-mt-6 md:block md:px-6 md:pt-6"
      >
        <EquitySnapshot
          :estimated-equity-available="estimatedEquityAvailable"
          :amount-owed="estimatedTotalAmountOwed"
          :is-ready="isEquityReady && isEstimatedTotalAmountOwedReady"
        />
      </div>

      <form @submit.prevent="handleSubmit">
        <!-- Purchase-only: read-only estimated funds required -->
        <div v-if="isPurchaseApplication" class="mb-6 mt-4">
          <BxNumberInput
            :model-value="baseLoanAmount"
            label="Funds required amount"
            label-type="floating"
            :tooltip="fundsRequiredAmountTooltip"
            leading-symbol="$"
            disabled
            readonly
            class="form-control"
          >
            <template #trailing>
              <button
                type="button"
                data-test-id="edit-funds-required"
                aria-label="Edit funds required"
                class="rounded text-bridgit-navy hover:text-bridgit-green focus:outline-none focus:ring-2 focus:ring-bridgit-green"
                @click="editFundsRequired"
              >
                <PencilSquareIcon class="h-5 w-5" aria-hidden="true" />
              </button>
            </template>
          </BxNumberInput>
        </div>

        <!-- Purchase-only: additional purposes — suppressed once contributions already cover the gap -->
        <template v-if="showAdditionalBorrowingPurposesQuestion">
          <BxField v-slot="{ field }" name="erhlAdditionalBorrowingPurposes">
            <BxMultiSelect
              v-bind="field"
              label="Additional borrowing purposes"
              label-type="floating"
              :tooltip="additionalBorrowingPurposesTooltip"
              :list-items="additionalPurposeOptions"
              class="form-control"
            />
          </BxField>

          <template v-if="hasAdditionalPurposes">
            <BxField v-if="hasOtherPurpose" v-slot="{ field }" name="erhlLoanPurposeOtherDescription">
              <BxTextarea
                v-bind="field"
                label="Provide the details of the additional borrowing purposes"
                label-type="floating"
                :rows="4"
                class="form-control"
              />
            </BxField>

            <BxField v-slot="{ field }" name="erhlAdditionalAmountToBorrow">
              <BxSlider
                v-if="isDesktop"
                id="additional-borrowing-slider"
                v-bind="field"
                label="Additional borrowing amount"
                label-type="floating"
                leading-symbol="$"
                hide-range-labels
                :min="0"
                :max="500_000"
                :step="1000"
                allow-out-of-range-input
                class="form-control"
              />
              <BxNumberInput
                v-else
                v-bind="field"
                label="Additional borrowing amount"
                label-type="floating"
                leading-symbol="$"
                class="form-control"
              />
            </BxField>
          </template>
        </template>

        <!-- Non-purchase: upfront lump sum fields -->
        <template v-if="!isPurchaseApplication">
          <div class="form-control mt-6">
            <div class="flex items-start gap-2">
              <h3 class="mb-2 font-medium text-bridgit-navy">Initial lump sum</h3>
              <button
                v-tippy="{ content: initialLumpSumTooltip, trigger: 'mouseenter focus', placement: 'right' }"
                type="button"
                class="shrink-0 text-bridgit-lightGrey hover:text-bridgit-darkGrey"
                aria-label="Initial lump sum information"
                @click.prevent
              >
                <BxIcon name="circleInfoLight" class="h-4 w-4" />
              </button>
            </div>
            <BxField v-slot="{ field }" name="lumpSumAmount">
              <BxSlider
                v-if="isDesktop"
                id="lump-sum-slider"
                v-bind="field"
                label="Initial lump sum"
                label-type="floating"
                hide-visual-label
                hide-range-labels
                leading-symbol="$"
                :min="ERHL_MIN_LOAN_AMOUNT"
                :max="initialLumpSumSliderMax"
                :step="10_000"
                allow-out-of-range-input
              />
              <BxNumberInput v-else v-bind="field" label="Initial lump sum" label-type="floating" leading-symbol="$" />
            </BxField>
          </div>
          <BxInlineNotice
            v-if="(values.lumpSumAmount ?? 0) > 0 && (values.lumpSumAmount ?? 0) < ERHL_MIN_LOAN_AMOUNT"
            class="mb-4"
          >
            Minimum loan amount is $300,000
          </BxInlineNotice>

          <BxField v-slot="{ field }" name="lumpSumFundsRequiredBy">
            <BxDatePicker
              v-bind="field"
              label="Funds required by?"
              label-type="floating"
              :tooltip="fundsRequiredByTooltip"
              class="form-control"
              :min-date="minFundsRequiredByDate"
              :unavailable-dates="unavailableFundsRequiredByDates"
            />
          </BxField>
        </template>

        <div class="form-control mt-6">
          <div class="flex items-start gap-2">
            <h3 class="font-medium text-bridgit-navy">Scheduled equity release?</h3>
            <button
              v-tippy="{ content: scheduledEquityReleaseTooltip, trigger: 'mouseenter focus', placement: 'right' }"
              type="button"
              class="shrink-0 text-bridgit-lightGrey hover:text-bridgit-darkGrey"
              aria-label="Scheduled equity release information"
              @click.prevent
            >
              <BxIcon name="circleInfoLight" class="h-4 w-4" />
            </button>
          </div>
          <BxField v-slot="{ field }" name="hasScheduledEquityRelease">
            <BxRadio v-bind="field" label="" boolean />
          </BxField>
        </div>

        <template v-if="values.hasScheduledEquityRelease">
          <BxField v-slot="{ field }" name="scheduledEquityReleaseAmount">
            <BxSlider
              v-if="isDesktop"
              id="amount-slider"
              v-bind="field"
              label="Amount"
              label-type="floating"
              leading-symbol="$"
              trailing-symbol="per month"
              hide-range-labels
              :min="1000"
              :max="20000"
              :step="1000"
              :tooltip="releaseAmountTooltip"
              allow-out-of-range-input
              class="form-control"
            />
            <BxNumberInput
              v-else
              v-bind="field"
              label="Amount"
              label-type="floating"
              leading-symbol="$"
              :tooltip="releaseAmountTooltip"
              class="form-control"
            >
              <template #trailing>per month</template>
            </BxNumberInput>
          </BxField>
          <BxInlineNotice v-if="showReleaseAmountPolicyNotice" class="mb-4">
            Above the $12,500 monthly policy maximum - an exception may be required.
          </BxInlineNotice>

          <BxField v-slot="{ field }" name="durationTotalYears" class="mt-6">
            <BxSlider
              v-if="isDesktop"
              id="duration-slider"
              v-bind="field"
              label="Duration"
              label-type="floating"
              :min="1"
              :max="scheduledEquityReleaseMaxDuration"
              :step="1"
              :tooltip="releaseTermTooltip"
              :trailing-symbol="scheduledEquityReleaseMaxDuration > 1 ? 'years' : 'year'"
              hide-range-labels
              allow-out-of-range-input
              class="form-control"
            />
            <BxNumberInput
              v-else
              v-bind="field"
              label="Duration"
              label-type="floating"
              :tooltip="releaseTermTooltip"
              class="form-control"
            >
              <template #trailing>{{ scheduledEquityReleaseMaxDuration > 1 ? 'years' : 'year' }}</template>
            </BxNumberInput>
          </BxField>
          <BxInlineNotice v-if="showReleaseTermPolicyNotice">
            Below the 10-year minimum term - an exception may be required.
          </BxInlineNotice>
          <p
            v-if="!showReleaseTermPolicyNotice && scheduledEquityReleaseMaxDuration > 15"
            class="mt-1 text-sm text-gray-500"
          >
            Minimum 10 years, maximum {{ scheduledEquityReleaseMaxDuration }} years
          </p>
        </template>

        <div v-if="showEquitySnapshot" data-test-id="equity-snapshot-mobile" class="mb-6 md:hidden">
          <hr class="mb-6 border-gray-200" />
          <div class="-mx-4 bg-[#F7FEF9] px-4">
            <EquitySnapshot
              :estimated-equity-available="estimatedEquityAvailable"
              :amount-owed="estimatedTotalAmountOwed"
              :is-ready="isEquityReady && isEstimatedTotalAmountOwedReady"
            />
          </div>
        </div>

        <div class="form-control mt-6 rounded-lg bg-green-50 p-4">
          <div class="flex items-start justify-between gap-4">
            <p class="min-w-0 flex-1 text-bridgit-green">
              {{ isPurchaseApplication ? 'Funds required for purchase' : 'Initial lump sum' }}
            </p>
            <p class="shrink-0 whitespace-nowrap font-bold text-bridgit-green">
              {{ formatCurrency(isPurchaseApplication ? baseLoanAmount : values.lumpSumAmount ?? 0) }}
            </p>
          </div>

          <div
            v-if="isPurchaseApplication && effectiveAdditionalAmountToBorrow > 0"
            class="mt-3 flex items-start justify-between gap-4"
          >
            <p class="min-w-0 flex-1 text-bridgit-green">Additional amount borrowed</p>
            <p class="shrink-0 whitespace-nowrap font-bold text-bridgit-green">
              {{ formatCurrency(effectiveAdditionalAmountToBorrow) }}
            </p>
          </div>

          <div
            v-if="!hasLoadedTotalScheduledEquityRelease || totalScheduledEquityRelease > 0"
            class="mt-3 flex items-start justify-between gap-4"
          >
            <p class="min-w-0 flex-1 text-bridgit-green">
              Total scheduled equity release
              <span v-if="values.hasScheduledEquityRelease" class="block text-xs text-gray-500">
                ({{ numberOfScheduledPayments }} monthly payments)
              </span>
            </p>
            <p class="shrink-0 whitespace-nowrap font-bold text-bridgit-green">
              <span v-if="hasLoadedTotalScheduledEquityRelease" class="text-bridgit-green">{{
                formatCurrency(totalScheduledEquityRelease)
              }}</span>
              <BxSpinner v-else class="ml-auto" />
            </p>
          </div>

          <hr class="my-3 border-gray-300" />

          <div class="flex items-start justify-between gap-4">
            <p class="min-w-0 flex-1 font-medium">
              Estimated balance at end of loan term{{ isPurchaseApplication ? '' : '*' }}
            </p>
            <p class="shrink-0 whitespace-nowrap text-lg font-bold text-bridgit-navy">
              <template v-if="isEstimatedTotalAmountOwedReady">{{ formatCurrency(estimatedTotalAmountOwed) }}</template>
              <BxSpinner v-else />
            </p>
          </div>
          <p v-if="!isPurchaseApplication" class="mt-1 text-xs text-gray-500">
            *Includes existing mortgage payout (if any) and applicable fees and charges.
          </p>
        </div>

        <div
          v-if="showEquityWarning"
          data-test-id="equity-warning"
          class="form-control mt-4 rounded-lg bg-orange-50 p-4 text-sm text-orange-700"
        >
          This is beyond the estimated equity available. If you require an amount beyond this, you are still able to
          submit the application pending confirmed valuation.
        </div>

        <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
          Please review the error messages above.
        </BxErrorPanel>

        <div class="mt-10">
          <BxButton variant="primary" :loading="isLoading" type="submit">Save and continue</BxButton>
          <BxButton v-if="state.nextEvents.includes('BACK')" variant="link" class="ml-4" @click="goBack()"
            >Back
          </BxButton>
        </div>
      </form>
    </div>
  </div>
</template>
